(() => {
  class MainArticleSection {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector('[data-article-richtext]');
      this.tocList = root.querySelector('[data-article-toc-list]');
      this.tocContainer = root.querySelector('[data-article-toc]');
      this.progressFill = root.querySelector('[data-article-progress-fill]');
      this.mobileStickyTemplate = root.querySelector('[data-article-mobile-sticky-template]');

      this.links = [];
      this.linksByHeadingId = new Map();
      this.currentActiveLink = null;
      this.pendingTargetId = null;
      this.pendingTargetTimer = null;
      this.frameRequested = false;
      this.isReady = false;

      this.onScroll = this.requestScrollUpdate.bind(this);
      this.onResize = this.requestScrollUpdate.bind(this);

      if (!this.content || !this.tocList || !this.tocContainer) return;

      this.isReady = true;
      this.init();
    }

    init() {
      this.replaceCalloutShortcodes();
      this.setupTocAndProgress();
    }

    destroy() {
      if (!this.isReady) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.pendingTargetTimer);
      this.frameRequested = false;
    }

    escapeHtml(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    toPlainText(rawHtml) {
      const parser = document.createElement('div');
      parser.innerHTML = rawHtml
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n');

      return (parser.textContent || '').replace(/\u00a0/g, ' ').replace(/\r/g, '').trim();
    }

    normalizeManualLineBreaks(value) {
      return value.replace(/\[br\]/gi, '\n\n').replace(/\\n/g, '\n\n').replace(/\/n/g, '\n\n');
    }

    renderBlueCallout(rawHtml) {
      const text = this.toPlainText(rawHtml);
      if (!text) return '';

      return `<div class="article-callout article-callout--blue"><p class="article-callout__text">${this.escapeHtml(text).replace(/\n/g, '<br>')}</p></div>`;
    }

    parseCreamFields(rawHtml) {
      const plain = this.normalizeManualLineBreaks(this.toPlainText(rawHtml));
      let title = '';
      let text = '';

      const textLabelMatch = plain.match(/(?:^|\n)\s*texte\s*:\s*([\s\S]*)$/i);
      if (textLabelMatch) {
        text = textLabelMatch[1].replace(/^\s+|\s+$/g, '');
        const beforeText = plain.slice(0, textLabelMatch.index);
        const titleLabelMatch = beforeText.match(/(?:^|\n)\s*titre\s*:\s*([\s\S]*)$/i);
        if (titleLabelMatch) {
          title = titleLabelMatch[1].replace(/^\s+|\s+$/g, '');
        }
      } else {
        const titleLabelMatch = plain.match(/(?:^|\n)\s*titre\s*:\s*([\s\S]*)$/i);
        if (titleLabelMatch) {
          title = titleLabelMatch[1].replace(/^\s+|\s+$/g, '');
        }
      }

      if (!title && !text) {
        const fallbackParts = plain
          .split(/\n{2,}/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (fallbackParts.length > 1) {
          title = fallbackParts[0];
          text = fallbackParts.slice(1).join('\n\n');
        } else if (fallbackParts.length === 1) {
          title = fallbackParts[0];
        }
      }

      return { title, text };
    }

    renderCreamCallout(rawHtml) {
      const { title, text } = this.parseCreamFields(rawHtml);
      if (!title && !text) return '';

      const titleMarkup = title ? `<p class="article-callout__title">${this.escapeHtml(title)}</p>` : '';
      const textMarkup = text ? `<p class="article-callout__text">${this.escapeHtml(text).replace(/\n/g, '<br>')}</p>` : '';

      return `<div class="article-callout article-callout--cream">${titleMarkup}${textMarkup}</div>`;
    }

    getCustomListItems(rawHtml) {
      const parser = document.createElement('div');
      parser.innerHTML = rawHtml;

      const htmlListItems = Array.from(parser.querySelectorAll('li'))
        .map((item) => (item.textContent || '').replace(/\u00a0/g, ' ').trim())
        .filter(Boolean);

      if (htmlListItems.length) return htmlListItems;

      return this.toPlainText(rawHtml)
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => line.replace(/^[-*•]\s+/, '').trim())
        .filter(Boolean);
    }

    renderCustomList(rawHtml) {
      const items = this.getCustomListItems(rawHtml);
      if (!items.length) return '';

      const icon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
          <path d="M8.07086 0C3.61317 0 0 3.61317 0 8.07086C0 12.5286 3.61317 16.1417 8.07086 16.1417C12.5286 16.1417 16.1417 12.5286 16.1417 8.07086C16.1417 3.61432 12.5297 0.00115216 8.07317 0H8.07086ZM12.2036 5.83913L7.01779 11.0492C6.89912 11.1736 6.73897 11.2497 6.5673 11.2635C6.38871 11.252 6.2228 11.1702 6.10413 11.0365L3.93462 8.86931C3.87355 8.80939 3.8724 8.71261 3.93116 8.65155L3.93462 8.64809L4.62476 7.95795C4.68237 7.89804 4.778 7.89573 4.83791 7.95334L4.84252 7.95795L6.56499 9.68043L11.2981 4.91165C11.3269 4.88169 11.366 4.86556 11.4064 4.86441C11.4478 4.86326 11.487 4.88054 11.5147 4.91165L12.1944 5.61331C12.259 5.66861 12.2659 5.76539 12.2106 5.82876C12.2083 5.83222 12.2048 5.83452 12.2013 5.83798L12.2036 5.83913Z" fill="#B2E2F7"/>
          <path d="M12.2036 5.83913L7.01779 11.0492C6.89912 11.1736 6.73897 11.2497 6.5673 11.2635C6.38871 11.252 6.2228 11.1702 6.10413 11.0365L3.93462 8.86931C3.87355 8.80939 3.8724 8.71261 3.93116 8.65155L3.93462 8.64809L4.62476 7.95795C4.68237 7.89804 4.778 7.89573 4.83791 7.95334L4.84252 7.95795L6.56499 9.68043L11.2981 4.91165C11.3269 4.88169 11.366 4.86556 11.4064 4.86441C11.4478 4.86326 11.487 4.88054 11.5147 4.91165L12.1944 5.61331C12.259 5.66861 12.2659 5.76539 12.2106 5.82876C12.2083 5.83222 12.2048 5.83452 12.2013 5.83798L12.2036 5.83913Z" fill="#092730"/>
        </svg>
      `;

      const itemsMarkup = items
        .map((item) => {
          return `<li class="article-custom-list__item"><span class="article-custom-list__icon">${icon}</span><span class="article-custom-list__text">${this.escapeHtml(item)}</span></li>`;
        })
        .join('');

      return `<ul class="article-custom-list">${itemsMarkup}</ul>`;
    }

    replaceCalloutShortcodes() {
      let html = this.content.innerHTML;
      const mobileStickyShortcodeRegex = /\[(?:mobile_sticky|sticky_mobile)\]/gi;
      const mobileStickyParagraphShortcodeRegex = /<p>\s*\[(?:mobile_sticky|sticky_mobile)\]\s*<\/p>/gi;

      if (this.mobileStickyTemplate) {
        const mobileStickyMarkup = `<div class="article-mobile-sticky">${this.mobileStickyTemplate.innerHTML}</div>`;
        let mobileStickyInserted = false;
        const replaceMobileSticky = () => {
          if (mobileStickyInserted) return '';
          mobileStickyInserted = true;
          return mobileStickyMarkup;
        };

        html = html.replace(mobileStickyParagraphShortcodeRegex, replaceMobileSticky);
        html = html.replace(mobileStickyShortcodeRegex, replaceMobileSticky);
      } else {
        html = html.replace(mobileStickyParagraphShortcodeRegex, '');
        html = html.replace(mobileStickyShortcodeRegex, '');
      }

      html = html.replace(
        /<p>\s*\[(?:liste|lsite)\]\s*<\/p>([\s\S]*?)<p>\s*\[\/(?:liste|lsite)\]\s*<\/p>/gi,
        (_, inner) => this.renderCustomList(inner)
      );
      html = html.replace(
        /<p>\s*\[(?:liste|lsite)\]([\s\S]*?)\[\/(?:liste|lsite)\]\s*<\/p>/gi,
        (_, inner) => this.renderCustomList(inner)
      );
      html = html.replace(
        /\[(?:liste|lsite)\]([\s\S]*?)\[\/(?:liste|lsite)\]/gi,
        (_, inner) => this.renderCustomList(inner)
      );

      html = html.replace(/<p>\s*\[blue\]\s*<\/p>([\s\S]*?)<p>\s*\[\/blue\]\s*<\/p>/gi, (_, inner) =>
        this.renderBlueCallout(inner)
      );
      html = html.replace(/<p>\s*\[blue\]([\s\S]*?)\[\/blue\]\s*<\/p>/gi, (_, inner) => this.renderBlueCallout(inner));
      html = html.replace(/\[blue\]([\s\S]*?)\[\/blue\]/gi, (_, inner) => this.renderBlueCallout(inner));

      html = html.replace(/<p>\s*\[cream\]\s*<\/p>([\s\S]*?)<p>\s*\[\/cream\]\s*<\/p>/gi, (_, inner) =>
        this.renderCreamCallout(inner)
      );
      html = html.replace(/<p>\s*\[cream\]([\s\S]*?)\[\/cream\]\s*<\/p>/gi, (_, inner) => this.renderCreamCallout(inner));
      html = html.replace(/\[cream\]([\s\S]*?)\[\/cream\]/gi, (_, inner) => this.renderCreamCallout(inner));

      this.content.innerHTML = html;
    }

    slugify(value) {
      return value
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    setActiveLink(activeLink) {
      if (!activeLink || this.currentActiveLink === activeLink) return;

      this.links.forEach((link) => {
        link.classList.toggle('is-active', link === activeLink);
      });
      this.currentActiveLink = activeLink;
    }

    getActiveHeading() {
      const activationLine = window.innerHeight * 0.3;
      let activeHeading = this.headings[0];

      for (const heading of this.headings) {
        if (heading.getBoundingClientRect().top <= activationLine) {
          activeHeading = heading;
        } else {
          break;
        }
      }

      return activeHeading;
    }

    updateReadingProgress() {
      if (!this.progressFill) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const rect = this.content.getBoundingClientRect();
      const start = window.scrollY + rect.top - viewportHeight * 0.2;
      const end = start + rect.height;
      const current = window.scrollY + viewportHeight * 0.4;

      if (end <= start) {
        this.progressFill.style.transform = 'scaleY(1)';
        return;
      }

      const progress = Math.max(0, Math.min(1, (current - start) / (end - start)));
      this.progressFill.style.transform = `scaleY(${progress})`;
    }

    updateActiveHeadingFromScroll() {
      this.frameRequested = false;
      this.updateReadingProgress();

      if (this.pendingTargetId) {
        const targetHeading = document.getElementById(this.pendingTargetId);
        if (targetHeading && targetHeading.getBoundingClientRect().top > window.innerHeight * 0.3 + 6) {
          const pendingLink = this.linksByHeadingId.get(this.pendingTargetId);
          if (pendingLink) this.setActiveLink(pendingLink);
          return;
        }

        this.pendingTargetId = null;
        window.clearTimeout(this.pendingTargetTimer);
      }

      const activeHeading = this.getActiveHeading();
      const activeLink = activeHeading ? this.linksByHeadingId.get(activeHeading.id) : null;
      if (activeLink) this.setActiveLink(activeLink);
    }

    requestScrollUpdate() {
      if (this.frameRequested) return;
      this.frameRequested = true;
      window.requestAnimationFrame(() => this.updateActiveHeadingFromScroll());
    }

    onTocLinkClick(event, heading, link) {
      event.preventDefault();
      this.pendingTargetId = heading.id;
      window.clearTimeout(this.pendingTargetTimer);
      this.pendingTargetTimer = window.setTimeout(() => {
        this.pendingTargetId = null;
      }, 1400);

      heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setActiveLink(link);

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', `#${heading.id}`);
      }
    }

    setupTocAndProgress() {
      this.headings = Array.from(this.content.querySelectorAll('h2')).filter((heading) => {
        return heading.textContent && heading.textContent.trim().length > 0;
      });

      this.tocList.innerHTML = '';
      this.links = [];
      this.linksByHeadingId.clear();

      if (!this.headings.length) {
        this.tocContainer.classList.add('is-empty');
        return;
      }

      this.tocContainer.classList.remove('is-empty');

      const usedSlugs = new Map();
      this.headings.forEach((heading, index) => {
        const title = heading.textContent.trim();
        const baseSlug = this.slugify(title) || `section-${index + 1}`;
        const currentCount = usedSlugs.get(baseSlug) || 0;
        usedSlugs.set(baseSlug, currentCount + 1);

        const headingSlug = currentCount ? `${baseSlug}-${currentCount + 1}` : baseSlug;
        heading.id = `article-${headingSlug}`;

        const link = document.createElement('a');
        link.className = 'article-toc__link p-small';
        link.href = `#${heading.id}`;
        link.textContent = title;
        link.addEventListener('click', (event) => this.onTocLinkClick(event, heading, link));

        this.tocList.appendChild(link);
        this.links.push(link);
        this.linksByHeadingId.set(heading.id, link);
      });

      const initialHash = window.location.hash.replace('#', '');
      if (initialHash && this.linksByHeadingId.has(initialHash)) {
        this.setActiveLink(this.linksByHeadingId.get(initialHash));
      }

      if (this.links[0]) {
        this.setActiveLink(this.currentActiveLink || this.links[0]);
      }

      this.updateActiveHeadingFromScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }
  }

  const instances = new Map();

  const initScope = (scope = document) => {
    const roots = scope.querySelectorAll('[data-section-type="main-article"]');
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new MainArticleSection(root);
      if (instance.isReady) {
        instances.set(root, instance);
      }
    });
  };

  const destroyScope = (scope = document) => {
    const roots = scope.querySelectorAll('[data-section-type="main-article"]');
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document));
  } else {
    initScope(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyScope(event.target);
  });
})();
